/**
 * Retry Service with Exponential Backoff
 * Handles automatic retry mechanisms for failed operations
 */

class RetryService {
  constructor() {
    this.defaultConfig = {
      maxRetries: 3,
      initialDelay: 1000, // 1 second
      maxDelay: 10000, // 10 seconds
      backoffMultiplier: 2,
      retryableErrors: [
        'Network error',
        'timeout',
        'PGRST301', // Supabase JWT expired
        'ECONNREFUSED',
        'ETIMEDOUT',
        '503', // Service unavailable
        '504', // Gateway timeout
        '408', // Request timeout
      ]
    };
  }

  /**
   * Check if error is retryable
   */
  isRetryableError(error) {
    if (!error) return false;
    
    const errorMessage = error?.message || error?.code || String(error);
    
    return this.defaultConfig?.retryableErrors?.some(retryableError => 
      errorMessage?.toLowerCase()?.includes(retryableError?.toLowerCase())
    );
  }

  /**
   * Calculate delay with exponential backoff
   */
  calculateDelay(attemptNumber, config = {}) {
    const { initialDelay, maxDelay, backoffMultiplier } = { ...this.defaultConfig, ...config };
    
    const delay = Math.min(
      initialDelay * Math.pow(backoffMultiplier, attemptNumber - 1),
      maxDelay
    );
    
    // Add jitter to prevent thundering herd
    const jitter = Math.random() * 0.3 * delay;
    
    return Math.floor(delay + jitter);
  }

  /**
   * Sleep for specified milliseconds
   */
  sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Execute operation with automatic retry
   */
  async executeWithRetry(operation, config = {}) {
    const { maxRetries, onRetry, onFailure } = { ...this.defaultConfig, ...config };
    
    let lastError = null;
    
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        console.log(`🔄 Retry attempt ${attempt}/${maxRetries}`);
        
        const result = await operation();
        
        // Success
        if (result?.error) {
          throw result?.error;
        }
        
        console.log(`✅ Operation succeeded on attempt ${attempt}`);
        return { success: true, data: result, attempt };
        
      } catch (error) {
        lastError = error;
        console.error(`❌ Attempt ${attempt} failed:`, error);
        
        // Check if error is retryable
        if (!this.isRetryableError(error)) {
          console.log('⚠️ Error is not retryable, stopping');
          onFailure?.(error, attempt);
          return { success: false, error, attempt, retryable: false };
        }
        
        // If this was the last attempt, don't wait
        if (attempt === maxRetries) {
          console.log('⚠️ Max retries reached');
          onFailure?.(error, attempt);
          return { success: false, error, attempt, retryable: true };
        }
        
        // Calculate delay and wait
        const delay = this.calculateDelay(attempt, config);
        console.log(`⏳ Waiting ${delay}ms before retry...`);
        
        onRetry?.(error, attempt, delay);
        
        await this.sleep(delay);
      }
    }
    
    return { success: false, error: lastError, attempt: maxRetries, retryable: true };
  }

  /**
   * Retry profile update operation
   */
  async retryProfileUpdate(updateFunction, updates, config = {}) {
    console.log('🔄 Starting profile update with retry mechanism');
    console.log('📝 Updates:', updates);
    
    const result = await this.executeWithRetry(
      () => updateFunction(updates),
      {
        ...config,
        onRetry: (error, attempt, delay) => {
          console.log(`🔄 Retry ${attempt}: ${error?.message}`);
          console.log(`⏳ Waiting ${delay}ms...`);
          config?.onRetry?.(error, attempt, delay);
        },
        onFailure: (error, attempt) => {
          console.log(`❌ All retries failed after ${attempt} attempts`);
          config?.onFailure?.(error, attempt);
        }
      }
    );
    
    return result;
  }

  /**
   * Retry database query operation
   */
  async retryQuery(queryFunction, config = {}) {
    console.log('🔄 Starting query with retry mechanism');
    
    const result = await this.executeWithRetry(
      queryFunction,
      config
    );
    
    return result;
  }
}

const retryService = new RetryService();
export default retryService;